import { useState, useEffect } from "react";
import BrandLogo from "@/components/BrandLogo";

const PageLoader = ({ children }: { children: React.ReactNode }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    // Only show loader on first visit. Skip entirely on mobile / coarse-pointer
    // devices to avoid the 1.5s opacity hold which hurts FCP/LCP on phones.
    const isMobile =
      typeof window !== "undefined" &&
      (window.innerWidth < 1024 || window.matchMedia("(pointer: coarse)").matches);
    const hasLoaded = sessionStorage.getItem("zaibify-loaded");
    if (hasLoaded || isMobile) {
      setIsLoading(false);
      setProgress(100);
      if (isMobile) sessionStorage.setItem("zaibify-loaded", "true");
      return;
    }

    // Fast 1.5-second loading experience
    const interval = setInterval(() => {
      setProgress((prev) => {
        if (prev >= 100) {
          clearInterval(interval);
          return 100;
        }
        return prev + 6.67;
      });
    }, 100);

    const timer = setTimeout(() => {
      setIsLoading(false);
      sessionStorage.setItem("zaibify-loaded", "true");
    }, 1500);

    return () => {
      clearTimeout(timer);
      clearInterval(interval);
    };
  }, []);

  return (
    <>
      {isLoading && (
        <div
          className="fixed inset-0 z-[200] flex flex-col items-center justify-center bg-background animate-fade-in"
        >
            {/* Background glow */}
            <div
              className="absolute inset-0 opacity-40"
              style={{
                background: "radial-gradient(ellipse at center, hsla(270, 70%, 60%, 0.1) 0%, transparent 60%)",
              }}
            />

            {/* Logo Container */}
            <div className="relative z-10 flex flex-col items-center">
              <div className="mb-8 animate-fade-in">
                <BrandLogo imgClassName="h-24 w-auto md:h-28" priority />
              </div>

              {/* Progress Bar */}
              <div className="w-64 md:w-80 animate-fade-in">
                <div className="flex justify-between items-center mb-3">
                  <span className="text-xs text-cream/68 tracking-widest uppercase">Loading</span>
                  <span className="text-xs text-primary font-medium">{Math.round(progress)}%</span>
                </div>

                <div className="relative h-[2px] bg-cream/10 rounded-full overflow-hidden">
                  <div
                    className="absolute inset-y-0 left-0 right-0 rounded-full origin-left will-change-transform"
                    style={{
                      transform: `scaleX(${progress / 100})`,
                      transition: 'transform 100ms linear',
                      background:
                        'linear-gradient(90deg, hsl(270, 70%, 60%), hsl(285, 75%, 62%), hsl(265, 65%, 55%))',
                    }}
                  />
                </div>
              </div>
            </div>
        </div>
      )}

      {/* Main content — render with no opacity wrapper when not loading so the
          LCP candidate paints immediately (fixes Lighthouse NO_LCP on mobile). */}
      {isLoading ? (
        <div style={{ opacity: 0 }}>{children}</div>
      ) : (
        children
      )}
    </>
  );
};

export default PageLoader;
